import { useCallback, useEffect, useState } from "react";

import {
  createComment,
  deleteComment,
  fetchComments,
  updateComment,
} from "../api/tas";
import type { ITasOccurrenceComment } from "../models/tasOccurrence";

const parseUtcIsoStringToDate = (value: string): Date | undefined => {
  let normalized = value;
  const hasTimezone = /Z$/.test(normalized) || /([+-]\d{2}:\d{2}|[+-]\d{2}\d{2})$/.test(normalized);
  if (!hasTimezone) normalized = `${normalized}Z`;
  normalized = normalized.replace(
    /(\.\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})$)/,
    "$1",
  );
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const normalizeComment = (raw: ITasOccurrenceComment): ITasOccurrenceComment => ({
  ...raw,
  createdAt:
    raw.createdAt instanceof Date
      ? raw.createdAt
      : typeof raw.createdAt === "string"
        ? parseUtcIsoStringToDate(raw.createdAt)
        : undefined,
});

export const useTasOccurrenceChat = (occurrenceId: string) => {
  const [comments, setComments] = useState<ITasOccurrenceComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCommentText, setNewCommentText] = useState("");
  const [isNewComment, setIsNewComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [submittingNew, setSubmittingNew] = useState(false);
  const [savingEditUuid, setSavingEditUuid] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<ITasOccurrenceComment | null>(null);

  const loadComments = useCallback(async () => {
    if (!occurrenceId) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await fetchComments(occurrenceId);
      const list = (data ?? []).map(normalizeComment);
      setComments(list.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [occurrenceId]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    setEditingCommentId(null);
    setEditDraft("");
  }, [occurrenceId]);

  const submitNewComment = async () => {
    const trimmed = newCommentText.trim();
    if (!occurrenceId || !trimmed || submittingNew) return;

    setSubmittingNew(true);
    setIsNewComment(true);

    try {
      const created = await createComment(occurrenceId, trimmed);
      if (created?.id) {
        setComments((prev) => [...prev, normalizeComment(created)].sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
        setNewCommentText("");
      } else {
        await loadComments();
        setNewCommentText("");
      }
    } finally {
      setSubmittingNew(false);
    }
  };

  const beginEdit = (comment: ITasOccurrenceComment) => {
    const commentId = comment.id;
    if (!commentId) return;
    setEditingCommentId(commentId);
    setEditDraft(comment.comment ?? "");
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    if (!occurrenceId || !editingCommentId) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    setSavingEditUuid(editingCommentId);
    const updated = await updateComment(occurrenceId, editingCommentId, trimmed);
    setSavingEditUuid(null);
    if (updated?.id) {
      setComments((prev) =>
        prev.map((c) => (c.id === editingCommentId ? normalizeComment(updated) : c)).sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
      );
      cancelEdit();
    } else {
      await loadComments();
      cancelEdit();
    }
  };

  const removeComment = async (commentId: number) => {
    if (!occurrenceId || !commentId) return;
    setDeletingId(commentId);
    await deleteComment(occurrenceId, commentId);
    setDeletingId(null);
    await loadComments();
  };

  return {
    comments,
    loading,
    newCommentText,
    submittingNew,
    editingCommentId,
    editDraft,
    savingEditUuid,
    deletingId,
    showConfirmDialog,
    commentToDelete,
    isNewComment,

    setNewCommentText,
    setEditDraft,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    setShowConfirmDialog,
    setCommentToDelete,
    reloadComments: loadComments,
  };
};

export type TasOccurrenceChatController = ReturnType<typeof useTasOccurrenceChat>;
